/**
 * Authorization Code Flow with PKCE — compatible with both SPA and Web platforms.
 * Frontend generates code_verifier + code_challenge, then sends verifier to backend
 * which does the actual token exchange using client_secret + verifier.
 */
const TENANT_ID = process.env.REACT_APP_AZURE_TENANT_ID;
const CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID;

export const isSsoConfigured = Boolean(TENANT_ID && CLIENT_ID);

const STATE_KEY    = 'smc_sso_state';
const VERIFIER_KEY = 'smc_sso_verifier';

const bytesToHex = (arr) =>
  Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');

const base64UrlEncode = (arrBuf) => {
  const bytes = new Uint8Array(arrBuf);
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const randomString = (len = 32) => {
  const arr = new Uint8Array(len);
  window.crypto.getRandomValues(arr);
  return bytesToHex(arr);
};

/** PKCE — generate a cryptographically random verifier (43–128 chars) */
const generatePkceVerifier = () => {
  // 48 random bytes → 64 base64url chars (within the 43–128 spec)
  const arr = new Uint8Array(48);
  window.crypto.getRandomValues(arr);
  return base64UrlEncode(arr.buffer);
};

/** PKCE — SHA-256 → base64url of the verifier */
const generatePkceChallenge = async (verifier) => {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

export const getRedirectUri = () => window.location.origin;

/** Builds the /authorize URL and persists state + PKCE verifier for the callback */
export const buildAuthorizeUrl = async () => {
  if (!isSsoConfigured) throw new Error('SSO no configurado');

  const state        = randomString(16);
  const codeVerifier = generatePkceVerifier();
  const codeChallenge = await generatePkceChallenge(codeVerifier);

  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, codeVerifier);

  const params = new URLSearchParams({
    client_id:             CLIENT_ID,
    response_type:         'code',
    redirect_uri:          getRedirectUri(),
    response_mode:         'query',
    scope:                 'openid profile email',
    state,
    prompt:                'select_account',
    code_challenge:        codeChallenge,
    code_challenge_method: 'S256',
  });

  return `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/authorize?${params.toString()}`;
};

/**
 * Reads `?code=...&state=...` from the current URL, validates state,
 * retrieves the matching PKCE verifier, and cleans both URL and storage.
 * Returns { code, code_verifier, error } or null.
 */
export const consumeCallback = () => {
  const params  = new URLSearchParams(window.location.search);
  const code    = params.get('code');
  const state   = params.get('state');
  const error   = params.get('error');
  const errDesc = params.get('error_description');

  if (!code && !error) return null;

  // Clean URL regardless of outcome
  const cleanUrl = window.location.origin + window.location.pathname;
  window.history.replaceState({}, document.title, cleanUrl);

  if (error) {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    return { error: errDesc || error };
  }

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const codeVerifier  = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!expectedState || expectedState !== state) {
    return { error: 'Estado SSO inválido (posible CSRF). Vuelve a iniciar sesión.' };
  }
  if (!codeVerifier) {
    return { error: 'Falta el verificador PKCE. Vuelve a iniciar sesión.' };
  }

  return { code, code_verifier: codeVerifier };
};

export const startSsoRedirect = async () => {
  const url = await buildAuthorizeUrl();
  window.location.href = url;
};
